import { useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "unixorn_admin_token";

const ERROR_KEYS = new Set(["timeout", "network_error", "exception"]);
const isErrorStatus = (s) => !!s && (s.startsWith("error_") || ERROR_KEYS.has(s));
const isRetryable = (s) => !s || s === "pending" || s === "not_configured" || isErrorStatus(s);

const STATUS_STYLES = {
  synced: "bg-green-500/20 text-green-300 border-green-500/30",
  pending: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  not_configured: "bg-stone-500/20 text-stone-300 border-stone-500/30",
  timeout: "bg-red-500/20 text-red-300 border-red-500/30",
  network_error: "bg-red-500/20 text-red-300 border-red-500/30",
  exception: "bg-red-500/20 text-red-300 border-red-500/30",
};

const statusBadgeClass = (statusValue) => {
  if (!statusValue) return STATUS_STYLES.not_configured;
  if (statusValue.startsWith("error_")) return STATUS_STYLES.exception;
  return STATUS_STYLES[statusValue] || STATUS_STYLES.not_configured;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const LoginPanel = ({ tokenInput, setTokenInput, onSubmit, error, busy }) => (
  <div className="min-h-screen bg-black text-[#F4EFE6] flex items-center justify-center px-6">
    <form
      onSubmit={onSubmit}
      className="w-full max-w-sm"
      data-testid="admin-login-form"
    >
      <p className="text-[11px] tracking-[0.4em] uppercase text-[#F4EFE6]/50 mb-5 text-center">
        UNIXORN — Internal
      </p>
      <h1 className="font-display text-3xl font-bold tracking-tight text-center mb-12">
        ADMIN
      </h1>
      <input
        type="password"
        value={tokenInput}
        onChange={(e) => setTokenInput(e.target.value)}
        placeholder="ADMIN TOKEN"
        autoFocus
        disabled={busy}
        data-testid="admin-token-input"
        className="w-full bg-transparent border border-[#F4EFE6]/30 text-[#F4EFE6] placeholder:text-[#F4EFE6]/40 placeholder:tracking-[0.25em] placeholder:text-xs h-14 px-5 outline-none focus:border-[#F4EFE6] transition-colors uppercase tracking-widest text-sm mb-4"
      />
      <button
        type="submit"
        disabled={busy || !tokenInput}
        data-testid="admin-login-submit"
        className="w-full bg-[#F4EFE6] text-black hover:bg-white h-14 transition-colors uppercase tracking-[0.3em] font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? "..." : "ENTER"}
      </button>
      {error && (
        <p
          data-testid="admin-login-error"
          className="mt-5 text-xs tracking-widest uppercase text-red-300/80 text-center"
        >
          {error}
        </p>
      )}
    </form>
  </div>
);

const StatCard = ({ label, value, accent }) => (
  <div className="border border-[#F4EFE6]/15 px-6 py-5">
    <p className="text-[10px] tracking-[0.35em] uppercase text-[#F4EFE6]/50 mb-2">
      {label}
    </p>
    <p
      className={`font-display text-3xl font-bold ${accent || "text-[#F4EFE6]"}`}
      data-testid={`admin-stat-${label.toLowerCase()}`}
    >
      {value}
    </p>
  </div>
);

const SubscriberRow = ({ subscriber, token, onRefresh }) => {
  const [retrying, setRetrying] = useState(false);
  const handleRetry = async () => {
    setRetrying(true);
    try {
      const res = await fetch(
        `${API}/api/admin/subscribers/${subscriber.id}/retry-klaviyo`,
        {
          method: "POST",
          headers: { "x-admin-token": token },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        toast.success("Synced to Klaviyo", { description: subscriber.email });
      } else {
        toast.error("Klaviyo sync failed", {
          description: `${subscriber.email} — ${data.klaviyo_status || data.detail || `HTTP ${res.status}`}`,
        });
      }
    } catch (err) {
      console.error("Retry failed:", err);
      toast.error("Network error", { description: subscriber.email });
    } finally {
      setRetrying(false);
      onRefresh();
    }
  };

  return (
    <div
      data-testid="admin-subscriber-row"
      className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#F4EFE6]/5 last:border-0 text-sm hover:bg-[#F4EFE6]/[0.02] transition-colors items-center"
    >
      <div className="col-span-4 break-all text-[#F4EFE6]/90">{subscriber.email}</div>
      <div className="col-span-2 text-xs tracking-wider uppercase text-[#F4EFE6]/50">
        {subscriber.source || "—"}
      </div>
      <div className="col-span-2">
        <span
          className={`inline-block px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase border ${statusBadgeClass(subscriber.klaviyo_status)}`}
        >
          {subscriber.klaviyo_status || "unknown"}
        </span>
      </div>
      <div className="col-span-2 text-xs text-[#F4EFE6]/40">
        {formatDate(subscriber.timestamp)}
      </div>
      <div className="col-span-2 flex justify-end">
        {isRetryable(subscriber.klaviyo_status) ? (
          <button
            onClick={handleRetry}
            disabled={retrying}
            data-testid="admin-retry-btn"
            className="border border-[#F4EFE6]/30 px-3 h-8 text-[10px] uppercase tracking-[0.25em] hover:bg-[#F4EFE6]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {retrying ? "..." : "Retry"}
          </button>
        ) : (
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#F4EFE6]/20">—</span>
        )}
      </div>
    </div>
  );
};

const Dashboard = ({ stats, subs, onLogout, onRefresh, refreshing, token }) => (
  <div className="min-h-screen bg-black text-[#F4EFE6] px-6 md:px-12 py-16">
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-12">
        <div>
          <p className="text-[11px] tracking-[0.4em] uppercase text-[#F4EFE6]/50 mb-3">
            UNIXORN — Internal
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            THE LIST — ADMIN
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="admin-refresh"
            className="border border-[#F4EFE6]/30 px-5 h-11 text-xs uppercase tracking-[0.3em] hover:bg-[#F4EFE6]/10 transition-colors disabled:opacity-50"
          >
            {refreshing ? "..." : "Refresh"}
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="border border-[#F4EFE6]/30 px-5 h-11 text-xs uppercase tracking-[0.3em] hover:bg-[#F4EFE6]/10 transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <StatCard label="TOTAL" value={stats?.total ?? 0} />
        <StatCard label="SYNCED" value={stats?.synced ?? 0} accent="text-green-300" />
        <StatCard label="PENDING" value={stats?.pending ?? 0} accent="text-yellow-300" />
        <StatCard label="ERRORS" value={stats?.errors ?? 0} accent="text-red-300" />
      </div>

      <div className="border border-[#F4EFE6]/15">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#F4EFE6]/10 text-[10px] tracking-[0.3em] uppercase text-[#F4EFE6]/50">
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Source</div>
          <div className="col-span-2">Klaviyo</div>
          <div className="col-span-2">Captured</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {subs.length === 0 ? (
          <p className="px-6 py-10 text-sm text-[#F4EFE6]/40 italic">
            No subscribers yet.
          </p>
        ) : (
          subs.map((s) => (
            <SubscriberRow
              key={s.id || s.email}
              subscriber={s}
              token={token}
              onRefresh={onRefresh}
            />
          ))
        )}
      </div>
    </div>
  </div>
);

const useAdminData = (token, onUnauthorized) => {
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    const run = async () => {
      setRefreshing(true);
      try {
        const headers = { "x-admin-token": token };
        const [statsRes, subsRes] = await Promise.all([
          fetch(`${API}/api/admin/stats`, { headers }),
          fetch(`${API}/api/subscribers`, { headers }),
        ]);
        if (!active) return;
        if (statsRes.status === 401 || subsRes.status === 401) {
          onUnauthorized();
          return;
        }
        const [statsData, subsData] = await Promise.all([
          statsRes.json(),
          subsRes.json(),
        ]);
        if (!active) return;
        setStats(statsData);
        setSubs(
          [...subsData].sort((a, b) =>
            (b.timestamp || "").localeCompare(a.timestamp || "")
          )
        );
      } catch (err) {
        console.error("Admin fetch failed:", err);
      } finally {
        if (active) setRefreshing(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [token, refreshCounter]);

  const refresh = () => setRefreshCounter((n) => n + 1);
  return { stats, subs, refreshing, refresh };
};

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [loginError, setLoginError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleUnauthorized = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLoginError("Session expired. Please re-enter your token.");
  };

  const { stats, subs, refreshing, refresh } = useAdminData(token, handleUnauthorized);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/api/admin/verify`, {
        method: "POST",
        headers: { "x-admin-token": tokenInput },
      });
      if (res.ok) {
        sessionStorage.setItem(TOKEN_KEY, tokenInput);
        setToken(tokenInput);
        setTokenInput("");
      } else {
        setLoginError("Invalid token.");
      }
    } catch (err) {
      console.error("Admin login failed:", err);
      setLoginError("Network error. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) {
    return (
      <LoginPanel
        tokenInput={tokenInput}
        setTokenInput={setTokenInput}
        onSubmit={handleLogin}
        error={loginError}
        busy={busy}
      />
    );
  }

  return (
    <Dashboard
      stats={stats}
      subs={subs}
      onLogout={handleLogout}
      onRefresh={refresh}
      refreshing={refreshing}
      token={token}
    />
  );
}
